import {BaseBadge, BaseButton, BaseCard, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {
  useCountryLevelLimits,
  useCreateCountryLevelLimit,
  useDeleteCountryLevelLimit,
  useUpdateCountryLevelLimit,
} from "@/hooks/useLevels";
import type {CountryLevelLimit, CreateCountryLevelLimitBody, UpdateCountryLevelLimitBody} from "@/types/levels.types";
import {Pause, Pencil, Play, Plus, Search, Trash2} from "lucide-react";
import {useState} from "react";
import {LEVEL_BADGE_VARIANT, LEVEL_NAMES, formatCents} from "../levels.utils";

export function CountryLimitsTab() {
  const [countryId, setCountryId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<CountryLevelLimit | null>(null);

  const {data: limits, isLoading} = useCountryLevelLimits(countryId);
  const deleteMutation = useDeleteCountryLevelLimit();
  const updateMutation = useUpdateCountryLevelLimit();

  const items = limits ?? [];

  const handleSearch = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const val = searchInput.trim().toUpperCase();
    if (val) setCountryId(val);
  };

  const columns: Column<CountryLevelLimit>[] = [
    {
      key: "level",
      header: "Nivel",
      render: row => (
        <BaseBadge
          variant={LEVEL_BADGE_VARIANT[row.level] ?? "neutral"}
          label={LEVEL_NAMES[row.level] ?? `Level ${row.level}`}
        />
      ),
    },
    {
      key: "paymentMethodType",
      header: "Método de Pago",
      render: row => <span className="text-sm text-(--color-text-primary)">{row.paymentMethodType}</span>,
    },
    {
      key: "maxPerTransactionCents",
      header: "Máx/Transacción",
      render: row => <span className="text-sm font-mono">{formatCents(row.maxPerTransactionCents)}</span>,
    },
    {
      key: "maxDailyCents",
      header: "Máx/Día",
      render: row => <span className="text-sm font-mono">{formatCents(row.maxDailyCents)}</span>,
    },
    {
      key: "maxMonthlyCents",
      header: "Máx/Mes",
      render: row => (
        <span className="text-sm font-mono">{row.maxMonthlyCents ? formatCents(row.maxMonthlyCents) : "—"}</span>
      ),
    },
    {
      key: "currency",
      header: "Moneda",
      render: row => <span className="text-sm font-medium">{row.currency}</span>,
    },
    {
      key: "isActive",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={row.isActive ? "success" : "neutral"}
          label={row.isActive ? "Activo" : "Inactivo"}
          dot
        />
      ),
    },
    {
      key: "actions",
      header: "Acciones",
      align: "right",
      render: row => (
        <div className="flex items-center gap-1 justify-end">
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => setEditing(row)}
            title="Editar"
          >
            <Pencil size={14} />
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => updateMutation.mutate({limitId: row.id, body: {isActive: !row.isActive}})}
            title={row.isActive ? "Desactivar" : "Activar"}
          >
            {row.isActive ? <Pause size={14} /> : <Play size={14} />}
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm(`¿Eliminar límite ${row.paymentMethodType} (${row.currency})?`)) {
                deleteMutation.mutate(row.id);
              }
            }}
            title="Eliminar"
          >
            <Trash2 size={14} />
          </BaseButton>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <BaseCard padding="md">
        <div className="flex items-center gap-4">
          <form
            onSubmit={handleSearch}
            className="flex items-center gap-2"
          >
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-(--color-text-tertiary)"
              />
              <input
                type="text"
                placeholder="Código de país (ej. US, MX)"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value.toUpperCase())}
                maxLength={20}
                className="pl-8 pr-3 py-2 rounded-md border border-(--color-border) bg-white text-sm text-(--color-text-primary) w-56"
              />
            </div>
            <BaseButton
              variant="primary"
              size="sm"
            >
              Buscar
            </BaseButton>
          </form>
          {countryId && (
            <BaseButton
              variant="secondary"
              size="sm"
              onClick={() => setShowCreate(true)}
            >
              <Plus size={14} /> Nuevo límite
            </BaseButton>
          )}
        </div>
      </BaseCard>

      {countryId ? (
        <BaseCard padding="none">
          <BaseTable<CountryLevelLimit>
            data={items}
            columns={columns}
            loading={isLoading}
            emptyMessage={`No hay límites configurados para ${countryId}.`}
          />
        </BaseCard>
      ) : (
        <BaseCard padding="lg">
          <p className="text-center text-(--color-text-tertiary)">
            Ingresa un código de país para ver los límites configurados.
          </p>
        </BaseCard>
      )}

      {showCreate && countryId && (
        <CreateCountryLimitModal
          countryId={countryId}
          onClose={() => setShowCreate(false)}
        />
      )}
      {editing && (
        <EditCountryLimitModal
          limit={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ── Create Modal ──────────────────────────────────────────── */

function CreateCountryLimitModal({countryId, onClose}: {countryId: string; onClose: () => void}) {
  const createMutation = useCreateCountryLevelLimit();
  const [form, setForm] = useState({
    level: "0",
    paymentMethodType: "",
    maxPerTransactionCents: "",
    maxDailyCents: "",
    maxMonthlyCents: "",
    currency: "",
    isActive: true,
  });
  const [error, setError] = useState("");

  const set = (key: string, value: string | boolean) => setForm(prev => ({...prev, [key]: value}));

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");

    if (!form.paymentMethodType || !form.maxPerTransactionCents || !form.maxDailyCents || !form.currency) {
      setError("Los campos marcados son requeridos");
      return;
    }

    const body: CreateCountryLevelLimitBody = {
      countryId,
      level: Number(form.level),
      paymentMethodType: form.paymentMethodType,
      maxPerTransactionCents: String(Math.round(Number(form.maxPerTransactionCents) * 100)),
      maxDailyCents: String(Math.round(Number(form.maxDailyCents) * 100)),
      ...(form.maxMonthlyCents ? {maxMonthlyCents: String(Math.round(Number(form.maxMonthlyCents) * 100))} : {}),
      currency: form.currency.toUpperCase(),
      isActive: form.isActive,
    };

    try {
      await createMutation.mutateAsync(body);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear el límite");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4">Nuevo Límite — {countryId}</h2>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <p className="text-xs text-(--color-text-tertiary) -mt-2">Los montos se ingresan en dólares.</p>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-text-secondary)">Nivel</span>
              <select
                value={form.level}
                onChange={e => set("level", e.target.value)}
                className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
              >
                <option value="0">0 — BASIC</option>
                <option value="1">1 — REGULAR</option>
                <option value="2">2 — VERIFIED</option>
              </select>
            </label>
            <Field
              label="Moneda *"
              value={form.currency}
              onChange={v => set("currency", v)}
              placeholder="USD"
            />
          </div>

          <Field
            label="Método de pago *"
            value={form.paymentMethodType}
            onChange={v => set("paymentMethodType", v)}
            placeholder="bank_transfer"
          />

          <div className="grid grid-cols-3 gap-3">
            <Field
              label="Máx/Tx ($) *"
              value={form.maxPerTransactionCents}
              onChange={v => set("maxPerTransactionCents", v)}
              type="number"
              step="0.01"
            />
            <Field
              label="Máx/Día ($) *"
              value={form.maxDailyCents}
              onChange={v => set("maxDailyCents", v)}
              type="number"
              step="0.01"
            />
            <Field
              label="Máx/Mes ($)"
              value={form.maxMonthlyCents}
              onChange={v => set("maxMonthlyCents", v)}
              type="number"
              step="0.01"
            />
          </div>

          <Toggle
            label="Activo"
            checked={form.isActive}
            onChange={v => set("isActive", v)}
          />

          {error && <p className="text-sm text-(--color-danger)">{error}</p>}

          <div className="flex justify-end gap-2 mt-2">
            <BaseButton
              variant="ghost"
              size="md"
              onClick={onClose}
            >
              Cancelar
            </BaseButton>
            <BaseButton
              variant="primary"
              size="md"
              loading={createMutation.isPending}
            >
              Crear
            </BaseButton>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Edit Modal ────────────────────────────────────────────── */

function EditCountryLimitModal({limit, onClose}: {limit: CountryLevelLimit; onClose: () => void}) {
  const updateMutation = useUpdateCountryLevelLimit();
  const [form, setForm] = useState({
    level: String(limit.level),
    paymentMethodType: limit.paymentMethodType,
    maxPerTransactionCents: String(Number(limit.maxPerTransactionCents) / 100),
    maxDailyCents: String(Number(limit.maxDailyCents) / 100),
    maxMonthlyCents: limit.maxMonthlyCents ? String(Number(limit.maxMonthlyCents) / 100) : "",
    currency: limit.currency,
    isActive: limit.isActive,
  });
  const [error, setError] = useState("");

  const set = (key: string, value: string | boolean) => setForm(prev => ({...prev, [key]: value}));

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");

    const body: UpdateCountryLevelLimitBody = {
      level: Number(form.level),
      paymentMethodType: form.paymentMethodType,
      maxPerTransactionCents: String(Math.round(Number(form.maxPerTransactionCents) * 100)),
      maxDailyCents: String(Math.round(Number(form.maxDailyCents) * 100)),
      ...(form.maxMonthlyCents ? {maxMonthlyCents: String(Math.round(Number(form.maxMonthlyCents) * 100))} : {}),
      currency: form.currency.toUpperCase(),
      isActive: form.isActive,
    };

    try {
      await updateMutation.mutateAsync({limitId: limit.id, body});
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar el límite");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4">Editar Límite — {limit.countryId}</h2>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <p className="text-xs text-(--color-text-tertiary) -mt-2">Los montos se ingresan en dólares.</p>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-(--color-text-secondary)">Nivel</span>
              <select
                value={form.level}
                onChange={e => set("level", e.target.value)}
                className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
              >
                <option value="0">0 — BASIC</option>
                <option value="1">1 — REGULAR</option>
                <option value="2">2 — VERIFIED</option>
              </select>
            </label>
            <Field
              label="Moneda"
              value={form.currency}
              onChange={v => set("currency", v)}
            />
          </div>

          <Field
            label="Método de pago"
            value={form.paymentMethodType}
            onChange={v => set("paymentMethodType", v)}
          />

          <div className="grid grid-cols-3 gap-3">
            <Field
              label="Máx/Tx ($)"
              value={form.maxPerTransactionCents}
              onChange={v => set("maxPerTransactionCents", v)}
              type="number"
              step="0.01"
            />
            <Field
              label="Máx/Día ($)"
              value={form.maxDailyCents}
              onChange={v => set("maxDailyCents", v)}
              type="number"
              step="0.01"
            />
            <Field
              label="Máx/Mes ($)"
              value={form.maxMonthlyCents}
              onChange={v => set("maxMonthlyCents", v)}
              type="number"
              step="0.01"
            />
          </div>

          <Toggle
            label="Activo"
            checked={form.isActive}
            onChange={v => set("isActive", v)}
          />

          {error && <p className="text-sm text-(--color-danger)">{error}</p>}

          <div className="flex justify-end gap-2 mt-2">
            <BaseButton
              variant="ghost"
              size="md"
              onClick={onClose}
            >
              Cancelar
            </BaseButton>
            <BaseButton
              variant="primary"
              size="md"
              loading={updateMutation.isPending}
            >
              Guardar
            </BaseButton>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Shared ────────────────────────────────────────────────── */

function Field({
  label,
  value,
  onChange,
  type = "text",
  step,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-(--color-text-secondary)">{label}</span>
      <input
        type={type}
        step={step}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
      />
    </label>
  );
}

function Toggle({label, checked, onChange}: {label: string; checked: boolean; onChange: (v: boolean) => void}) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-(--color-border) accent-(--color-primary)"
      />
      <span className="text-sm text-(--color-text-secondary)">{label}</span>
    </label>
  );
}
